import classNames from 'classnames';
import { Fragment } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import useShowMoreLinks from '@alltrails/layout/hooks/useShowMoreLinks';
import type { SeoLinkItem } from '@alltrails/types/SeoLinkItem';
import LinkSection from './LinkSection';
import styles from './SeoLinks.module.scss';
import { CURATED_HOMEPAGE_MAX_LINKS_PER_SECTION, DEFAULT_MAX_LINKS_PER_SECTION } from './seoLinksLimits';

const SEO_SECTION_STRINGS = defineMessages({
  TRAILS: {
    defaultMessage: 'Top trails'
  },
  PARKS: {
    defaultMessage: 'Top parks'
  },
  CITIES: {
    defaultMessage: 'Top cities'
  },
  REGIONS: {
    defaultMessage: 'Top regions'
  },
  STATES: {
    defaultMessage: 'Top states'
  }
});

type SectionKey = 'REGIONS' | 'STATES' | 'TRAILS' | 'PARKS' | 'CITIES' | 'POIS';

type CuratedSeoLinkSectionProps = {
  sectionKey: SectionKey;
  links: SeoLinkItem[];
  isBot?: boolean;
  getHeading: (sectionKey: SectionKey) => string;
};

function CuratedSeoLinkSection({ sectionKey, links, isBot = false, getHeading }: CuratedSeoLinkSectionProps) {
  const { visibleLinkItems, showMoreSlot, isExpanded } = useShowMoreLinks({
    linkItems: links,
    collapsedLimit: DEFAULT_MAX_LINKS_PER_SECTION,
    isBot
  });
  const linkLayoutLimit =
    isExpanded || (isBot && links.length > DEFAULT_MAX_LINKS_PER_SECTION) ? CURATED_HOMEPAGE_MAX_LINKS_PER_SECTION : DEFAULT_MAX_LINKS_PER_SECTION;
  const heading = getHeading(sectionKey);
  return <LinkSection links={visibleLinkItems} heading={heading} limit={linkLayoutLimit} showMoreToggle={showMoreSlot} />;
}

export type SeoLinks = {
  key: SectionKey;
  data: SeoLinkItem[];
}[];

export type SeoLinksProps = {
  data: SeoLinks;
  className?: string;
  showAdditionalLinks?: boolean;
  isBot?: boolean;
};

// Desktop: https://www.figma.com/file/deyiqSQ1gfAajm667k20DU/SEO---Increase-internal-links?node-id=54%3A33765
// Mobile: https://www.figma.com/file/deyiqSQ1gfAajm667k20DU/SEO---Increase-internal-links?node-id=8%3A12152
const SeoLinks = ({ data, className, showAdditionalLinks = false, isBot = false }: SeoLinksProps) => {
  const intl = useIntl();
  const maxLinksPerSection = showAdditionalLinks ? CURATED_HOMEPAGE_MAX_LINKS_PER_SECTION : DEFAULT_MAX_LINKS_PER_SECTION;

  const sectionHeading = (sectionKey: SectionKey) =>
    // POIS is only sent for en-US
    sectionKey === 'POIS' ? `Top points of interest` : intl.formatMessage(SEO_SECTION_STRINGS[sectionKey]);

  return (
    <>
      <div
        className={classNames(styles.container, className, {
          [styles.containerCurated]: showAdditionalLinks
        })}
      >
        <Typography component="h2" variant="heading500">
          <FormattedMessage defaultMessage="Adventure anywhere" />
        </Typography>
        <div className={styles.linkSection}>
          {data.map(section => {
            if (section.data.length === 0) return null;
            // The max number of links sent from the backend is 15, except for homepage curated lists where we send 50 links
            const links = section.data.slice(0, maxLinksPerSection);

            if (!showAdditionalLinks) {
              const heading = sectionHeading(section.key);
              return <LinkSection links={links} heading={heading} key={section.key} limit={maxLinksPerSection} />;
            }

            return (
              <Fragment key={section.key}>
                <CuratedSeoLinkSection getHeading={sectionHeading} links={links} sectionKey={section.key} isBot={isBot} />
              </Fragment>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default SeoLinks;
